import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Eye, Printer, X, ClipboardList, Trash2, Calendar, Pencil } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pull the logged-in user from localStorage. We only need `role` and `token`
// for the admin-gated date editor; if anything is missing we default to a
// non-admin shape so the editor stays hidden.
const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem('currentUser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Convert an ISO timestamp into the local "YYYY-MM-DD" + "HH:MM" pieces a
// <input type="date"> / <input type="time"> expect. Returns blank strings
// if the input is missing or invalid.
const splitIsoForInputs = (iso) => {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

// Combine the date + time inputs back into a Date object representing the
// user's local moment in time. Returns null if either piece is missing.
const combineLocalDateTime = (date, time) => {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ==================== STATIC OPTION LISTS ====================
const BEHAVIOR_OPTIONS = [
  'Calm/Stable mood',
  'Appropriate peer interaction',
  'Irritability',
  'Depressive symptoms',
  'Anxiety/excess worry',
  'Isolation/Withdrawn from peers',
  'Substance cravings/urges',
  'Noncompliance w/ house rules',
  'Difficulty completing ADLs',
  'Sleep concerns',
  'Physical pain/medical complaints',
  'Passive SI/HI (no plan)',
  'Psychotic symptoms',
  'Financial/benefit-related stress',
  'Interpersonal conflict',
];
const MEDICATION_OPTIONS = [
  'Medications taken as scheduled',
  'Medication questions or concerns reported',
  'Missed/refused dose (reported to appropriate staff)',
  'Health complaint mentioned (non-urgent)',
];
const INTERVENTION_OPTIONS = [
  'Orientation to Residential Setting and House Expectations',
  'Safety-Oriented Pat-Down and Belongings Review',
  'Nursing Assessment/Medical Coordination',
  'Medication Administration',
  'Skills Restoration/Daily-Living Coaching',
  'Daily Structure/Routine Support',
  'Psychoeducation Group',
  'Community Activity',
  'Craving-Management Check-In',
  'Withdrawal Symptom Monitoring',
  'Recovery-Oriented Encouragement',
  'Mental Health Check-in/Mental Health Monitoring',
  'Coping Skills Reminders',
  'Redirection and De-Escalation Support',
  'Safety Planning/Risk Assessment',
  'Discharge Planning/House Departure',
];
const SAFETY_OPTIONS = ['No concerns', 'Passive SI', 'HI', 'Cravings'];

// ==================== HELPERS ====================
const blankNote = () => ({
  id: '',
  createdAt: '',
  staffName: '',
  staffCredentials: '',
  clientId: '',
  clientName: '',
  medicaidNumber: '',
  location: '',
  staffCount: '',
  residentCount: '',
  shiftNumber: '',
  shiftDate: new Date().toISOString().slice(0, 10),
  startTime: '',
  endTime: '',
  totalHours: '',
  behaviorsObserved: [],
  behaviorOther: '',
  medicationStatus: [],
  medicationCompliant: '',
  medicationExplanation: '',
  interventions: [],
  interventionOther: '',
  adlHygiene: '',
  adlRoom: '',
  adlMeals: '',
  adlSleep: '',
  adlNotes: '',
  safetyConcerns: [],
  safetyOther: '',
  safetyNotes: '',
  clientBehaviorResponse: '',
  planFollowUp: '',
  signatureConfirmed: false,
  signedAt: '',
});

const toggleInArray = (arr, value) =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const calcHours = (start, end) => {
  if (!start || !end) return '';
  // Accept either 24-hour ("15:00") or 12-hour ("3:00 PM") input.
  // Returns minutes since midnight, or null if unparseable.
  const parse = (raw) => {
    const s = String(raw).trim().toLowerCase();
    if (!s) return null;
    // Try AM/PM first ("3:00 pm", "3pm", "3:00pm", "11:30 a.m.")
    const ampm = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)$/);
    if (ampm) {
      let h = parseInt(ampm[1], 10);
      const m = parseInt(ampm[2] || '0', 10);
      const isPm = ampm[3].startsWith('p');
      if (h === 12) h = 0;
      if (isPm) h += 12;
      if (h < 0 || h > 23 || m < 0 || m > 59) return null;
      return h * 60 + m;
    }
    // Fallback: 24-hour "HH:MM"
    const h24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (h24) {
      const h = parseInt(h24[1], 10);
      const m = parseInt(h24[2], 10);
      if (h < 0 || h > 23 || m < 0 || m > 59) return null;
      return h * 60 + m;
    }
    return null;
  };
  const startMin = parse(start);
  const endMin = parse(end);
  if (startMin === null || endMin === null) return '';
  let mins = endMin - startMin;
  if (mins < 0) mins += 24 * 60; // crosses midnight
  return (mins / 60).toFixed(2);
};

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const ShiftNotes = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentUser = useMemo(() => getCurrentUser(), []);
  const isAdmin = currentUser?.role === 'admin';

  const [notes, setNotes] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [editingDate, setEditingDate] = useState(null); // note being date-edited
  const [draft, setDraft] = useState(blankNote);
  const [signedFilter, setSignedFilter] = useState('all');

  const refreshNotes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shift-notes`);
      setNotes(data || []);
    } catch (e) {
      console.error('Failed to fetch shift notes', e);
    }
  };

  // Initial fetch from the backend
  useEffect(() => {
    refreshNotes();
  }, []);

  // Auto-recalc total hours when start/end change
  useEffect(() => {
    setDraft((d) => ({ ...d, totalHours: calcHours(d.startTime, d.endTime) }));
  }, [draft.startTime, draft.endTime]);

  // Schedule integration: open form pre-filled when arriving with ?new=1&...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      const prefill = blankNote();
      if (params.get('date')) prefill.shiftDate = params.get('date');
      if (params.get('startTime')) prefill.startTime = params.get('startTime');
      if (params.get('endTime')) prefill.endTime = params.get('endTime');
      if (params.get('staffName')) prefill.staffName = params.get('staffName');
      if (params.get('shiftNumber')) prefill.shiftNumber = params.get('shiftNumber');
      setDraft(prefill);
      setFormOpen(true);
      // Clear the query string so refresh doesn't re-open the form
      navigate('/shift-notes', { replace: true });
    }
  }, []); // eslint-disable-line

  const handleNew = () => {
    setDraft(blankNote());
    setFormOpen(true);
  };

  const handleSave = async () => {
    // Section 8 e-signature validation
    if (!draft.staffName.trim()) {
      alert('Please type your full name in the Staff Sign-Off section before saving.');
      return;
    }
    if (!draft.signatureConfirmed) {
      alert('Please check the e-signature confirmation box in the Staff Sign-Off section before saving.');
      return;
    }

    // Server stamps id + createdAt; we send the rest of the form draft.
    // Strip the empty id/createdAt placeholders so the server controls them.
    const { id: _omit, createdAt: _omit2, ...rest } = draft;
    const payload = { ...rest, signedAt: new Date().toISOString() };
    try {
      await axios.post(`${API_URL}/api/shift-notes`, payload);
      setFormOpen(false);
      await refreshNotes();
    } catch (e) {
      console.error('Failed to save shift note', e);
      alert('Could not save shift note. Please try again.');
    }
  };

  const handleView = (note) => {
    setActiveNote(note);
    setViewOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shift note? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/shift-notes/${id}`);
      if (activeNote?.id === id) {
        setActiveNote(null);
        setViewOpen(false);
      }
      await refreshNotes();
    } catch (e) {
      console.error('Failed to delete shift note', e);
      alert('Could not delete shift note. Please try again.');
    }
  };

  // Admin-only: PATCH the signed_at timestamp on an existing note. Updates
  // local state immediately (table row, view dialog, active note) so the new
  // value is visible without a refetch.
  const handleSaveDate = async (noteId, newIsoString) => {
    const token = currentUser?.token;
    const updated = await axios.patch(
      `${API_URL}/api/shift-notes/${noteId}`,
      { signed_at: newIsoString },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    const fresh = updated.data;
    // Reflect the change in any view that's currently mounted
    setNotes((prev) => prev.map((n) => (n.id === noteId ? fresh : n)));
    setActiveNote((prev) => (prev?.id === noteId ? fresh : prev));
    setEditingDate(null);
  };

  const visibleNotes = useMemo(() => {
    const sorted = [...notes].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    if (signedFilter === 'signed')   return sorted.filter(n => n.signedAt && n.staffName);
    if (signedFilter === 'unsigned') return sorted.filter(n => !(n.signedAt && n.staffName));
    return sorted;
  }, [notes, signedFilter]);

  // ==================== PRINT ====================
  const handlePrint = () => {
    if (!activeNote) return;
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) {
      alert('Pop-up blocked. Please allow pop-ups for this site to print.');
      return;
    }
    w.document.open();
    w.document.write(buildPrintHTML(activeNote));
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="shift-notes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Shift Notes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            ACTS 3.1 Shift Note documentation
          </p>
        </div>
        <Button onClick={handleNew} data-testid="new-shift-note-button">
          <Plus className="h-4 w-4 mr-1" />
          Log New Note
        </Button>
      </div>

      {notes.length === 0 ? (
        <Card className="p-12 text-center" data-testid="shift-notes-empty">
          <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-base text-muted-foreground">No shift notes logged yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Click <span className="font-medium">Log New Note</span> to get started.
          </p>
        </Card>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <Select value={signedFilter} onValueChange={setSignedFilter}>
              <SelectTrigger
                className="w-[180px]"
                data-testid="shift-notes-filter-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-option-all">All</SelectItem>
                <SelectItem value="signed" data-testid="filter-option-signed">Signed</SelectItem>
                <SelectItem value="unsigned" data-testid="filter-option-unsigned">Unsigned</SelectItem>
              </SelectContent>
            </Select>
            <p
              className="text-sm text-muted-foreground"
              data-testid="shift-notes-filter-count"
            >
              Showing {visibleNotes.length} of {notes.length} notes
            </p>
          </div>

          {visibleNotes.length === 0 ? (
            <Card className="p-12 text-center" data-testid="shift-notes-empty-filtered">
              <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-base text-muted-foreground">No notes match this filter.</p>
            </Card>
          ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Date</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Staff</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Signed By</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Client</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Shift</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Unit</th>
                <th className="text-right p-3 font-semibold text-xs uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleNotes.map((n) => (
                <tr key={n.id} className="border-t hover:bg-muted/20" data-testid={`note-row-${n.id}`}>
                  <td className="p-3">{n.shiftDate || '—'}</td>
                  <td className="p-3 font-medium">{n.staffName || '—'}</td>
                  <td className="p-3" data-testid={`note-signed-${n.id}`}>
                    {n.signedAt && n.staffName ? (
                      <span
                        className="font-signature"
                        style={{ fontSize: '20px' }}
                        data-testid={`note-signed-name-${n.id}`}
                      >
                        {n.staffName}
                      </span>
                    ) : (
                      <Badge
                        variant="destructive"
                        className="text-[10px]"
                        data-testid={`note-unsigned-${n.id}`}
                      >
                        Unsigned
                      </Badge>
                    )}
                  </td>
                  <td className="p-3">{n.clientName || '—'}</td>
                  <td className="p-3">
                    {n.shiftNumber ? <Badge variant="secondary">{n.shiftNumber}</Badge> : '—'}
                  </td>
                  <td className="p-3">{n.location || '—'}</td>
                  <td className="p-3 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => handleView(n)} data-testid={`view-note-${n.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" />View
                    </Button>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingDate(n)}
                        data-testid={`edit-date-row-${n.id}`}
                        title="Edit signed date/time (admin only)"
                      >
                        <Calendar className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(n.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
          )}
        </>
      )}

      <NoteFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        draft={draft}
        setDraft={setDraft}
        onSave={handleSave}
      />

      <NoteViewDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        note={activeNote}
        onPrint={handlePrint}
        isAdmin={isAdmin}
        onEditDate={(note) => setEditingDate(note)}
      />

      <EditDateDialog
        note={editingDate}
        onClose={() => setEditingDate(null)}
        onSave={handleSaveDate}
      />
    </div>
  );
};

// ==================== FORM DIALOG ====================
const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">
      {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const RadioGroup = ({ name, value, onChange, options }) => (
  <div className="flex flex-wrap gap-3">
    {options.map((opt) => (
      <label key={opt} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
        <input
          type="radio"
          name={name}
          value={opt}
          checked={value === opt}
          onChange={(e) => onChange(e.target.value)}
          className="accent-primary"
        />
        {opt}
      </label>
    ))}
  </div>
);

const CheckboxList = ({ options, values, onToggle, columns = 2 }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-${columns} gap-2`}>
    {options.map((opt) => (
      <label key={opt} className="inline-flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={values.includes(opt)}
          onChange={() => onToggle(opt)}
          className="accent-primary mt-0.5"
        />
        <span>{opt}</span>
      </label>
    ))}
  </div>
);

const NoteFormDialog = ({ open, onClose, draft, setDraft, onSave }) => {
  const set = (k) => (v) => setDraft({ ...draft, [k]: v });
  const setEvt = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="note-form-dialog">
        <DialogHeader>
          <DialogTitle>Log Shift Note</DialogTitle>
          <DialogDescription>
            ACTS 3.1 Shift Note — fill out each section as applicable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* === SESSION INFO === */}
          <Section title="1. Session Info">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Client Name">
                <Input value={draft.clientName} onChange={setEvt('clientName')} data-testid="form-clientName" />
              </Field>
              <Field label="Client ID">
                <Input value={draft.clientId} onChange={setEvt('clientId')} />
              </Field>
              <Field label="Medicaid #">
                <Input value={draft.medicaidNumber} onChange={setEvt('medicaidNumber')} />
              </Field>
            </div>
            <Field label="Location">
              <RadioGroup
                name="location"
                value={draft.location}
                onChange={set('location')}
                options={['Unit 1', 'Unit 2', 'Unit 3']}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="# of Staff">
                <Input type="number" min="0" value={draft.staffCount} onChange={setEvt('staffCount')} />
              </Field>
              <Field label="# of Residents">
                <Input type="number" min="0" value={draft.residentCount} onChange={setEvt('residentCount')} />
              </Field>
            </div>
            <Field label="Shift">
              <RadioGroup
                name="shiftNumber"
                value={draft.shiftNumber}
                onChange={set('shiftNumber')}
                options={['1st', '2nd', '3rd']}
              />
            </Field>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Date">
                <Input type="date" value={draft.shiftDate} onChange={setEvt('shiftDate')} />
              </Field>
              <Field label="Start Time">
                <Input
                  type="text"
                  inputMode="text"
                  placeholder="e.g. 3:00 PM"
                  value={draft.startTime}
                  onChange={setEvt('startTime')}
                />
              </Field>
              <Field label="End Time">
                <Input
                  type="text"
                  inputMode="text"
                  placeholder="e.g. 11:00 PM"
                  value={draft.endTime}
                  onChange={setEvt('endTime')}
                />
              </Field>
              <Field label="Total Hours">
                <Input value={draft.totalHours} readOnly className="bg-muted/40" />
              </Field>
            </div>
          </Section>

          {/* === BEHAVIORS === */}
          <Section title="2. Behaviors Observed (check all that apply)">
            <CheckboxList
              options={BEHAVIOR_OPTIONS}
              values={draft.behaviorsObserved}
              onToggle={(v) => set('behaviorsObserved')(toggleInArray(draft.behaviorsObserved, v))}
              columns={2}
            />
            <Field label="Other (specify)">
              <Input value={draft.behaviorOther} onChange={setEvt('behaviorOther')} placeholder="Optional" />
            </Field>
          </Section>

          {/* === MEDICATION === */}
          <Section title="3. Medication Status">
            <CheckboxList
              options={MEDICATION_OPTIONS}
              values={draft.medicationStatus}
              onToggle={(v) => set('medicationStatus')(toggleInArray(draft.medicationStatus, v))}
              columns={1}
            />
            <Field label="Compliant since previous shift?">
              <RadioGroup
                name="medicationCompliant"
                value={draft.medicationCompliant}
                onChange={set('medicationCompliant')}
                options={['Yes', 'No', 'N/A']}
              />
            </Field>
            <Field label="Explanation">
              <Textarea rows={2} value={draft.medicationExplanation} onChange={setEvt('medicationExplanation')} />
            </Field>
          </Section>

          {/* === INTERVENTIONS === */}
          <Section title="4. Interventions Provided (check all that apply)">
            <CheckboxList
              options={INTERVENTION_OPTIONS}
              values={draft.interventions}
              onToggle={(v) => set('interventions')(toggleInArray(draft.interventions, v))}
              columns={2}
            />
            <Field label="Other (specify)">
              <Input value={draft.interventionOther} onChange={setEvt('interventionOther')} placeholder="Optional" />
            </Field>
          </Section>

          {/* === ADL === */}
          <Section title="5. ADL / Functioning">
            <Field label="Personal hygiene">
              <RadioGroup
                name="adlHygiene"
                value={draft.adlHygiene}
                onChange={set('adlHygiene')}
                options={['Yes', 'No', 'Needs prompting', 'Declined']}
              />
            </Field>
            <Field label="Room maintained">
              <RadioGroup
                name="adlRoom"
                value={draft.adlRoom}
                onChange={set('adlRoom')}
                options={['Yes', 'Needs prompting']}
              />
            </Field>
            <Field label="Meals eaten">
              <RadioGroup
                name="adlMeals"
                value={draft.adlMeals}
                onChange={set('adlMeals')}
                options={['All', 'Partial', 'Refused']}
              />
            </Field>
            <Field label="Sleep readiness">
              <RadioGroup
                name="adlSleep"
                value={draft.adlSleep}
                onChange={set('adlSleep')}
                options={['Appropriate', 'Difficulty winding down']}
              />
            </Field>
            <Field label="Notes">
              <Textarea rows={3} value={draft.adlNotes} onChange={setEvt('adlNotes')} />
            </Field>
          </Section>

          {/* === RISK / SAFETY === */}
          <Section title="6. Risk / Safety">
            <CheckboxList
              options={SAFETY_OPTIONS}
              values={draft.safetyConcerns}
              onToggle={(v) => set('safetyConcerns')(toggleInArray(draft.safetyConcerns, v))}
              columns={3}
            />
            <Field label="Other (specify)">
              <Input value={draft.safetyOther} onChange={setEvt('safetyOther')} placeholder="Optional" />
            </Field>
            <Field label="If concerns: safety plan / staff response">
              <Textarea rows={3} value={draft.safetyNotes} onChange={setEvt('safetyNotes')} />
            </Field>
          </Section>

          {/* === NARRATIVE === */}
          <Section title="7. Narrative">
            <Field label="Client behaviors and response to interventions">
              <Textarea rows={4} value={draft.clientBehaviorResponse} onChange={setEvt('clientBehaviorResponse')} />
            </Field>
            <Field label="Plan / Follow-Up">
              <Textarea rows={4} value={draft.planFollowUp} onChange={setEvt('planFollowUp')} />
            </Field>
          </Section>

          {/* === SIGN-OFF === */}
          <Section title="8. Staff Sign-Off">
            <Field label="Type your full name (this becomes your e-signature)">
              <Input
                value={draft.staffName}
                onChange={setEvt('staffName')}
                placeholder="e.g. Brittany Chandler"
                data-testid="form-staffName"
              />
            </Field>

            {/* Live cursive preview on a ruled line */}
            <div data-testid="signature-preview-wrap">
              <span className="text-xs font-medium text-muted-foreground">
                Signature preview
              </span>
              <div
                className="mt-1 px-2 pt-3 pb-1 border-b border-foreground/40"
                style={{ minHeight: '52px' }}
              >
                {draft.staffName.trim() ? (
                  <span className="font-signature" data-testid="signature-preview">
                    {draft.staffName}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground italic">
                    Your signature will appear here as you type…
                  </span>
                )}
              </div>
            </div>

            {/* E-signature confirmation checkbox */}
            <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={draft.signatureConfirmed}
                onChange={(e) => set('signatureConfirmed')(e.target.checked)}
                className="accent-primary mt-0.5"
                data-testid="signature-confirm-checkbox"
              />
              <span>
                By signing, I confirm this shift note is accurate and complete.
                This constitutes my electronic signature.
              </span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <Field label="Credentials">
                <Input
                  value={draft.staffCredentials}
                  onChange={setEvt('staffCredentials')}
                  placeholder="e.g. RSS, BHT"
                />
              </Field>
              <Field label="Date">
                <Input type="date" value={draft.shiftDate} onChange={setEvt('shiftDate')} />
              </Field>
            </div>
          </Section>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} data-testid="save-shift-note-button">Save Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== VIEW DIALOG ====================
const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
    <span className="text-muted-foreground col-span-1">{label}</span>
    <span className="col-span-2 font-medium">{value || <span className="text-muted-foreground italic font-normal">—</span>}</span>
  </div>
);

const ViewSection = ({ title, children }) => (
  <div className="space-y-1">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">
      {title}
    </h3>
    <div className="pt-1">{children}</div>
  </div>
);

const ListBlock = ({ items, fallback = '—' }) => {
  if (!items || items.length === 0) {
    return <span className="text-sm text-muted-foreground italic">{fallback}</span>;
  }
  return (
    <ul className="list-disc list-inside text-sm space-y-0.5">
      {items.map((i) => <li key={i}>{i}</li>)}
    </ul>
  );
};

const NoteViewDialog = ({ open, onClose, note, onPrint, isAdmin, onEditDate }) => {
  if (!note) return null;
  const signedDisplay = note.signedAt
    ? new Date(note.signedAt).toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : (note.shiftDate || '—');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-note-dialog">
        <DialogHeader>
          <DialogTitle>Shift Note — {note.clientName || 'Unnamed Client'}</DialogTitle>
          <DialogDescription>
            {note.shiftDate} · Shift {note.shiftNumber} · {note.location}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <ViewSection title="1. Session Info">
            <Row label="Client Name" value={note.clientName} />
            <Row label="Client ID" value={note.clientId} />
            <Row label="Medicaid #" value={note.medicaidNumber} />
            <Row label="Location" value={note.location} />
            <Row label="# Staff / # Residents" value={`${note.staffCount || '—'} / ${note.residentCount || '—'}`} />
            <Row label="Shift" value={note.shiftNumber} />
            <Row label="Date" value={note.shiftDate} />
            <Row label="Start – End" value={`${note.startTime || '—'} – ${note.endTime || '—'}`} />
            <Row label="Total Hours" value={note.totalHours} />
          </ViewSection>

          <ViewSection title="2. Behaviors Observed">
            <ListBlock items={[...note.behaviorsObserved, note.behaviorOther].filter(Boolean)} />
          </ViewSection>

          <ViewSection title="3. Medication Status">
            <ListBlock items={note.medicationStatus} />
            <Row label="Compliant since previous shift" value={note.medicationCompliant} />
            <Row label="Explanation" value={note.medicationExplanation} />
          </ViewSection>

          <ViewSection title="4. Interventions Provided">
            <ListBlock items={[...note.interventions, note.interventionOther].filter(Boolean)} />
          </ViewSection>

          <ViewSection title="5. ADL / Functioning">
            <Row label="Personal hygiene" value={note.adlHygiene} />
            <Row label="Room maintained" value={note.adlRoom} />
            <Row label="Meals eaten" value={note.adlMeals} />
            <Row label="Sleep readiness" value={note.adlSleep} />
            <Row label="Notes" value={note.adlNotes} />
          </ViewSection>

          <ViewSection title="6. Risk / Safety">
            <ListBlock items={[...note.safetyConcerns, note.safetyOther].filter(Boolean)} />
            <Row label="Safety plan / staff response" value={note.safetyNotes} />
          </ViewSection>

          <ViewSection title="7. Narrative">
            <Row label="Client behaviors / response" value={note.clientBehaviorResponse} />
            <Row label="Plan / Follow-Up" value={note.planFollowUp} />
          </ViewSection>

          <ViewSection title="8. Staff Sign-Off">
            <div className="space-y-2">
              <div>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Signature</span>
                <div
                  className="mt-1 px-2 pt-3 pb-1 border-b border-foreground/40"
                  style={{ minHeight: '52px' }}
                >
                  {note.staffName ? (
                    <span className="font-signature" data-testid="view-signature">
                      {note.staffName}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">No signature</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground italic pt-1">
                By signing, I confirm this shift note is accurate and complete.
                This constitutes my electronic signature.
              </p>
              <Row label="Credentials" value={note.staffCredentials} />
              <div
                className="grid grid-cols-3 gap-2 py-1.5 text-sm items-center"
                data-testid="view-signed-row"
              >
                <span className="text-muted-foreground col-span-1">Date / Time Signed</span>
                <div className="col-span-2 flex items-center gap-2 flex-wrap">
                  <span className="font-medium" data-testid="view-signed-value">
                    {signedDisplay}
                  </span>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEditDate?.(note)}
                      className="h-7 px-2 text-xs"
                      data-testid="edit-date-button"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit Date
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </ViewSection>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onPrint} data-testid="print-note-button">
            <Printer className="h-4 w-4 mr-1" />
            Print Note
          </Button>
          <Button onClick={onClose}>
            <X className="h-4 w-4 mr-1" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== EDIT-DATE DIALOG ====================
// Admin-only mini editor for the signed_at timestamp on a saved note.
// Pre-fills the date + time inputs with the current value (interpreted in
// the browser's local timezone), then PATCHes the new value as ISO.
const EditDateDialog = ({ note, onClose, onSave }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Re-prime the inputs each time a different note is opened
  useEffect(() => {
    if (!note) return;
    const seed = splitIsoForInputs(note.signedAt) || { date: '', time: '' };
    // If the note has no signedAt at all, fall back to the shiftDate + 12:00
    const fallbackDate = note.shiftDate || new Date().toISOString().slice(0, 10);
    setDate(seed.date || fallbackDate);
    setTime(seed.time || '12:00');
    setError('');
    setSubmitting(false);
  }, [note]);

  if (!note) return null;

  const handleSubmit = async () => {
    const local = combineLocalDateTime(date, time);
    if (!local) {
      setError('Please choose a valid date and time.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      await onSave(note.id, local.toISOString());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 403) {
        setError('Only admins can edit the signed date.');
      } else {
        setError(detail || 'Could not update the signed date. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!note} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-date-dialog">
        <DialogHeader>
          <DialogTitle>Edit Signed Date / Time</DialogTitle>
          <DialogDescription>
            {note.clientName || 'Unnamed client'} · {note.staffName || 'Unsigned'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="edit-date-input"
              />
            </Field>
            <Field label="Time">
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="edit-time-input"
              />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">
            Updates the signed timestamp on this shift note. Admin-only action.
          </p>
          {error && (
            <p className="text-xs text-destructive" data-testid="edit-date-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="edit-date-save-button"
          >
            {submitting ? 'Saving…' : 'Save Date'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== PRINT HTML ====================
const buildPrintHTML = (n) => {
  const checkbox = (checked, label) =>
    `<div class="cb">${checked ? '☑' : '☐'} ${escapeHtml(label)}</div>`;

  const behaviorBlocks = BEHAVIOR_OPTIONS.map((opt) =>
    checkbox(n.behaviorsObserved.includes(opt), opt)
  ).join('');
  const otherBehavior = n.behaviorOther
    ? `<div class="cb">☑ Other: ${escapeHtml(n.behaviorOther)}</div>`
    : `<div class="cb">☐ Other: ___________________________</div>`;

  const medBlocks = MEDICATION_OPTIONS.map((opt) =>
    checkbox(n.medicationStatus.includes(opt), opt)
  ).join('');

  const interventionBlocks = INTERVENTION_OPTIONS.map((opt) =>
    checkbox(n.interventions.includes(opt), opt)
  ).join('');
  const otherIntervention = n.interventionOther
    ? `<div class="cb">☑ Other: ${escapeHtml(n.interventionOther)}</div>`
    : `<div class="cb">☐ Other: ___________________________</div>`;

  const safetyBlocks = SAFETY_OPTIONS.map((opt) =>
    checkbox(n.safetyConcerns.includes(opt), opt)
  ).join('');
  const otherSafety = n.safetyOther
    ? `<div class="cb">☑ Other: ${escapeHtml(n.safetyOther)}</div>`
    : `<div class="cb">☐ Other: ___________________________</div>`;

  const radio = (selected, value) => (selected === value ? '☑' : '☐');
  const blank = (val) =>
    val ? escapeHtml(val) : '<span class="blank-line">_______________</span>';
  const longBlank = (val) =>
    val
      ? `<div class="multiline">${escapeHtml(val)}</div>`
      : '<div class="blank-multiline">&nbsp;</div>';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>SHIFT NOTE — ${escapeHtml(n.clientName || 'Client')} — ${escapeHtml(n.shiftDate || '')}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600;700&display=swap" rel="stylesheet">
<style>
  @page {
    size: 8.5in 11in;
    margin: 1in;
  }
  @page {
    @bottom-right {
      content: "Page " counter(page) " of " counter(pages);
      font-size: 9pt;
      color: #444;
      font-family: Arial, sans-serif;
    }
  }
  * { box-sizing: border-box; }
  html, body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11pt;
    color: #000;
    background: #fff;
    margin: 0;
    padding: 0;
    line-height: 1.4;
  }
  .brand {
    text-align: center;
    font-size: 9pt;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #333;
  }
  h1.title {
    text-align: center;
    font-size: 18pt;
    font-weight: 700;
    letter-spacing: 2px;
    margin: 4px 0 14px 0;
  }
  h2.section {
    font-size: 11pt;
    font-weight: 700;
    margin: 14px 0 6px 0;
    padding-bottom: 3px;
    border-bottom: 1px solid #000;
    text-transform: uppercase;
    /* Avoid awkward break right after a section heading */
    page-break-after: avoid;
    break-after: avoid-page;
  }
  /* Keep each section's heading + content together when possible
     without forcing a fresh page (replaces the old .page-break rule). */
  h2.section + .row,
  h2.section + .cb-grid,
  h2.section + .cb,
  h2.section + .field,
  .row, .cb-grid, .cb, .field, .signoff {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .nowrap {
    white-space: nowrap;
  }
  .row {
    display: flex;
    gap: 18px;
    margin-bottom: 4px;
  }
  .row > .field {
    flex: 1;
  }
  .field label {
    font-size: 9pt;
    color: #444;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .field .val {
    border-bottom: 1px solid #888;
    padding: 1px 2px;
    min-height: 15pt;
  }
  .cb-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 14px;
    row-gap: 2px;
    margin: 4px 0;
  }
  .cb-grid.three { grid-template-columns: 1fr 1fr 1fr; }
  .cb-grid.full { grid-template-columns: 1fr; }
  .cb { font-size: 10.5pt; }
  .blank-line { display: inline-block; min-width: 120pt; border-bottom: 1px solid #888; }
  .blank-multiline {
    border-bottom: 1px solid #888;
    height: 36pt;
  }
  .multiline {
    white-space: pre-wrap;
    border-bottom: 1px solid #ddd;
    padding-bottom: 2px;
    min-height: 18pt;
  }
  .signoff {
    margin-top: 18pt;
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 18px;
  }
  .signoff .field .val {
    border-bottom: 1px solid #000;
    height: 20pt;
  }
  /* Typed e-signature block */
  .signature-block {
    margin-top: 10pt;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .signature-block .sig-label {
    font-size: 9pt;
    color: #444;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .signature-block .sig-line {
    border-bottom: 1px solid #000;
    min-height: 38pt;
    padding: 4pt 4pt 2pt 4pt;
    margin-top: 2pt;
  }
  .signature-block .sig-name {
    font-family: 'Dancing Script', 'Brush Script MT', 'Lucida Handwriting', cursive;
    font-weight: 700;
    font-size: 26pt;
    line-height: 1;
    color: #1a1a2e;
    letter-spacing: 0.5px;
  }
  .signature-block .sig-confirm {
    font-size: 9pt;
    font-style: italic;
    color: #333;
    margin-top: 6pt;
  }
  .signature-block .sig-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    margin-top: 8pt;
  }
  .signature-block .sig-meta .field .val {
    border-bottom: 1px solid #000;
    height: 18pt;
  }
</style>
</head>
<body>
  <div class="brand">ALIGN Integrated Care · ACTS 3.1 Supervised Living</div>
  <h1 class="title">Shift Note</h1>

  <h2 class="section">1. Session Info</h2>
  <div class="row">
    <div class="field"><label>Client Name</label><div class="val">${blank(n.clientName)}</div></div>
    <div class="field"><label>Client ID</label><div class="val">${blank(n.clientId)}</div></div>
    <div class="field"><label>Medicaid #</label><div class="val">${blank(n.medicaidNumber)}</div></div>
  </div>
  <div class="row">
    <div class="field"><label>Location</label><div class="val">
      ${radio(n.location, 'Unit 1')} Unit 1 &nbsp;
      ${radio(n.location, 'Unit 2')} Unit 2 &nbsp;
      ${radio(n.location, 'Unit 3')} Unit 3
    </div></div>
    <div class="field"><label># Staff</label><div class="val">${blank(n.staffCount)}</div></div>
    <div class="field"><label># Residents</label><div class="val">${blank(n.residentCount)}</div></div>
  </div>
  <div class="row">
    <div class="field"><label>Shift</label><div class="val nowrap">
      ${radio(n.shiftNumber, '1st')} 1st &nbsp;&nbsp;
      ${radio(n.shiftNumber, '2nd')} 2nd &nbsp;&nbsp;
      ${radio(n.shiftNumber, '3rd')} 3rd
    </div></div>
    <div class="field"><label>Date</label><div class="val">${blank(n.shiftDate)}</div></div>
  </div>
  <div class="row">
    <div class="field"><label>Start</label><div class="val">${blank(n.startTime)}</div></div>
    <div class="field"><label>End</label><div class="val">${blank(n.endTime)}</div></div>
    <div class="field"><label>Total Hrs</label><div class="val">${blank(n.totalHours)}</div></div>
  </div>

  <h2 class="section">2. Behaviors Observed</h2>
  <div class="cb-grid">${behaviorBlocks}</div>
  ${otherBehavior}

  <h2 class="section">3. Medication Status</h2>
  <div class="cb-grid full">${medBlocks}</div>
  <div class="cb">Compliant since previous shift:
    ${radio(n.medicationCompliant, 'Yes')} Yes &nbsp;
    ${radio(n.medicationCompliant, 'No')} No &nbsp;
    ${radio(n.medicationCompliant, 'N/A')} N/A
  </div>
  <div class="field" style="margin-top:4px">
    <label>Explanation</label>
    ${longBlank(n.medicationExplanation)}
  </div>

  <h2 class="section">4. Interventions Provided</h2>
  <div class="cb-grid">${interventionBlocks}</div>
  ${otherIntervention}

  <h2 class="section">5. ADL / Functioning</h2>
  <div class="cb">Personal hygiene:
    ${radio(n.adlHygiene, 'Yes')} Yes &nbsp;
    ${radio(n.adlHygiene, 'No')} No &nbsp;
    ${radio(n.adlHygiene, 'Needs prompting')} Needs prompting &nbsp;
    ${radio(n.adlHygiene, 'Declined')} Declined
  </div>
  <div class="cb">Room maintained:
    ${radio(n.adlRoom, 'Yes')} Yes &nbsp;
    ${radio(n.adlRoom, 'Needs prompting')} Needs prompting
  </div>
  <div class="cb">Meals eaten:
    ${radio(n.adlMeals, 'All')} All &nbsp;
    ${radio(n.adlMeals, 'Partial')} Partial &nbsp;
    ${radio(n.adlMeals, 'Refused')} Refused
  </div>
  <div class="cb">Sleep readiness:
    ${radio(n.adlSleep, 'Appropriate')} Appropriate &nbsp;
    ${radio(n.adlSleep, 'Difficulty winding down')} Difficulty winding down
  </div>
  <div class="field" style="margin-top:4px">
    <label>Notes</label>
    ${longBlank(n.adlNotes)}
  </div>

  <h2 class="section">6. Risk / Safety</h2>
  <div class="cb-grid three">${safetyBlocks}</div>
  ${otherSafety}
  <div class="field" style="margin-top:4px">
    <label>If concerns: safety plan / staff response</label>
    ${longBlank(n.safetyNotes)}
  </div>

  <h2 class="section">7. Narrative</h2>
  <div class="field">
    <label>Client behaviors and response to interventions</label>
    ${longBlank(n.clientBehaviorResponse)}
  </div>
  <div class="field" style="margin-top:8px">
    <label>Plan / Follow-Up</label>
    ${longBlank(n.planFollowUp)}
  </div>

  <h2 class="section">8. Staff Sign-Off</h2>
  <div class="signature-block">
    <div class="sig-label">Signature</div>
    <div class="sig-line">
      <span class="sig-name">${escapeHtml(n.staffName || '')}</span>
    </div>
    <div class="sig-confirm">
      By signing, I confirm this shift note is accurate and complete.
      This constitutes my electronic signature.
    </div>
    <div class="sig-meta">
      <div class="field">
        <label>Credentials</label>
        <div class="val">${blank(n.staffCredentials)}</div>
      </div>
      <div class="field">
        <label>Date / Time Signed</label>
        <div class="val">${escapeHtml(
          n.signedAt
            ? new Date(n.signedAt).toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : (n.shiftDate || '')
        )}</div>
      </div>
    </div>
  </div>

  <script>
    window.addEventListener('load', function() {
      // Wait for the Dancing Script web font to finish downloading so the
      // signature renders in cursive, not the fallback.
      var ready = (document.fonts && document.fonts.ready) || Promise.resolve();
      Promise.race([ready, new Promise(function(r){ setTimeout(r, 1500); })])
        .then(function(){ setTimeout(function(){ window.print(); }, 150); });
    });
  </script>
</body>
</html>`;
};

export default ShiftNotes;
